import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";

import { TIME_OFF_BALANCES_FILTERS } from "../../../assets/constants";
import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import axios from "../../../utils/api-client";
import { TimeOffBalancesReportState } from "./time-off-balances-report-types";

const initialState: TimeOffBalancesReportState = {
  balances: [],
  getTimeOffBalancesReport: {status: "idle", errorMessage: ""},
  filters: { locations: [], workcenters: [], shiftPatterns: [], timeOffTypes: [], reportingManagers: [], filterRelationships: []},
  getFilters: { status: "idle", errorMessage: "" },
  updateBalance: {
    status: "idle",
    errorMessage: ""
  },
};

interface BalanceObject {
  balanceMinutes: number;
  balanceFormatted: string;
}

export const convertHoursToBalance = (hours: number): BalanceObject => {
  const totalMinutes = Math.round(hours * 60);
  const isNegative = totalMinutes < 0;
  const absTotalMinutes = Math.abs(totalMinutes);

  const h = Math.floor(absTotalMinutes / 60);
  const m = absTotalMinutes % 60;

  const hourString = `${h} ${h !== 1 ? i18next.t("time-off-balances_hours") : i18next.t("time-off-balances_hour")}`;

  // Only create minute string if there are minutes
  const minuteString = m > 0 ? `${m} ${m !== 1 ? i18next.t("time-off-balances_mins") : i18next.t("time-off-balances_min")}` : "";

  // Combine parts, ensuring correct spacing only if minutes exist
  const balanceFormatted = `${isNegative ? "-" : ""}${hourString}${m > 0 ? " " + minuteString : ""}`;

  return {
    balanceMinutes: totalMinutes,
    balanceFormatted,
  };
};

// API Action for the main report data
export const getTimeOffBalancesReport = createAsyncThunk(
  "timeOffBalancesReport/getReport",
  async (_, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/Reports/GetTimeOffBalances`
      );
      return fulfillWithValue(res.data ?? []);
    } catch (error: any) {
      const errorResponse = error?.response;
      if (errorResponse?.status === 403) {
        return rejectWithValue(i18next.t("time-off-balances_feature-not-enabled"));
      }
      if (errorResponse?.status === 429) {
          dispatch(setSupervisorRateLimitError());
      }
      return rejectWithValue(i18next.t("time-off-balances_fetch-error"));
    }
  }
);

// API Action for the filter options
export const getTimeOffBalancesFilters = createAsyncThunk(
  "timeOffBalancesReport/getFilters",
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/FlexScheduler/GetFiltersForUser`,
        { filters: TIME_OFF_BALANCES_FILTERS }
      );
      if (res.status === 200) {
        return fulfillWithValue(res.data);
      }
      return rejectWithValue(i18next.t("time-off-balances_filters-error"));
    } catch (error: any) {
      return rejectWithValue(i18next.t("time-off-balances_filters-error"));
    }
  }
);

export const updateBalance = createAsyncThunk(
  "timeOffBalancesReport/updateBalance",
  async ({ workerId, leaveTypeId, availableHours, workerInfo }: any, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/workerleave/updatebalance`,
        { workerId, leaveTypeId, availableHours }
      );
      if (res.status === 200) {
        return fulfillWithValue({
          workerId,
          leaveTypeId,
          balanceMinutes: convertHoursToBalance(availableHours).balanceMinutes,
          balanceFormatted: convertHoursToBalance(availableHours).balanceFormatted,
          workerInfo
        });
      }
      return rejectWithValue(i18next.t("time-off-balances_update-error"));
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      return rejectWithValue(errorResponse?.message ?? i18next.t("time-off-balances_update-error"));
    }
  }
)

export const TimeOffBalancesReportSlice = createSlice({
  name: "timeOffBalancesReport",
  initialState,
  reducers: {
    resetUpdateBalance: (state) => {
      state.updateBalance.status = "idle";
      state.updateBalance.errorMessage = "";
    }
  },
  extraReducers: (builder) => {
    // Reducer for Report Data
    builder.addCase(getTimeOffBalancesReport.pending, (state) => {
      state.getTimeOffBalancesReport.status = "pending";
    });
    builder.addCase(getTimeOffBalancesReport.fulfilled, (state, action) => {
      const balancesData = Array.isArray(action.payload) ? action.payload : [];
      state.balances = balancesData.map((balance: any, index: number) => ({
          id: `${balance.workerId}-${balance.timeOffTypeId}-${index}`,
          ...balance,
      }));
      state.getTimeOffBalancesReport.status = "fulfilled";
    });
    builder.addCase(getTimeOffBalancesReport.rejected, (state, action: any) => {
      state.getTimeOffBalancesReport.status = "error";
      state.getTimeOffBalancesReport.errorMessage = action.payload ?? i18next.t("common_error-occurred");
    });

    // Reducer for Filter Data
    builder.addCase(getTimeOffBalancesFilters.pending, (state) => {
      state.getFilters.status = "pending";
    });
    builder.addCase(getTimeOffBalancesFilters.fulfilled, (state, action) => {
      state.filters = {
          locations: action.payload.locations || [],
          workcenters: action.payload.workCenters || [],
          shiftPatterns: action.payload.shiftPatterns || [],
          timeOffTypes: action.payload.leaveTypes || [],
          reportingManagers: action.payload.managers || [],
          filterRelationships: action.payload.filterRelationships ?? [],
      };
      state.getFilters.status = "fulfilled";
    });
    builder.addCase(getTimeOffBalancesFilters.rejected, (state, action: any) => {
      state.getFilters.status = "error";
      state.getFilters.errorMessage = action.payload ?? i18next.t("common_error-occurred");
    });

    // Reducer for Updating Leave Balance
    builder.addCase(updateBalance.pending, (state) => {
      state.updateBalance.status = "pending";
      state.updateBalance.errorMessage = "";
    });
    builder.addCase(updateBalance.fulfilled, (state, action) => {
      state.updateBalance.status = "fulfilled";
      state.updateBalance.errorMessage = "";
      
      const existingIndex = state.balances.findIndex(
        (balance) =>
          balance.workerId === action.payload.workerId &&
          balance.timeOffTypeId === action.payload.leaveTypeId
      );

      if (existingIndex !== -1) {
        // Update existing balance
        state.balances[existingIndex] = {
          ...state.balances[existingIndex],
          balanceFormatted: action.payload.balanceFormatted,
          balanceMinutes: action.payload.balanceMinutes,
        };
      } else if (action.payload.workerInfo) {
        // Add new balance record
        const { workerInfo } = action.payload;
        state.balances.push({
          id: `${action.payload.workerId}-${action.payload.leaveTypeId}-${state.balances.length}`,
          workerId: action.payload.workerId,
          timeOffTypeId: action.payload.leaveTypeId,
          timeOffType: workerInfo.timeOffType,
          balanceMinutes: action.payload.balanceMinutes,
          balanceFormatted: action.payload.balanceFormatted,
          companyEmployeeId: workerInfo.companyEmployeeId,
          fullName: workerInfo.fullName,
          badgeNumber: workerInfo.badgeNumber,
          jobTitle: workerInfo.jobTitle,
          workcenter: workerInfo.workcenter,
          location: workerInfo.location,
          shiftPattern: workerInfo.shiftPattern,
          reportingManager: workerInfo.reportingManager,
          isNoLimit: workerInfo.isNoLimit,
        });
      }
    });
    builder.addCase(updateBalance.rejected, (state, action) => {
      state.updateBalance.status = "error";
      state.updateBalance.errorMessage = action.payload ?? i18next.t("common_error-occurred");
    })
  },
});

export const { resetUpdateBalance } = TimeOffBalancesReportSlice.actions;

export default TimeOffBalancesReportSlice.reducer;